import React, { useState } from 'react';
import Logo from '../../assets/icons/CampusLLMLogoSmall.png';
import { useAuth } from "../../context/AuthContext";
import {useLocation, useNavigate} from "react-router-dom";
import {useOrganization} from "../../context/OrgContext";
import {LuChevronRight, LuShare2} from "react-icons/lu";
import InviteModal from '../modals/admin/InviteModal'; // Adjust the import path as needed

export default function TopNav({ assignedUsers = [] }) {
    const { currentUser, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [imageError, setImageError] = useState(false);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const { institution } = useOrganization()

    const handleProfileClick = () => {
        //navigate("/profile");
        logout();
        navigate("/login")
    };

    const handleShareClick = () => {
        setIsInviteModalOpen(true);
    };

    const handleCloseInviteModal = () => {
        setIsInviteModalOpen(false);
    };

    const showModule = (institution?.superAdmin === currentUser?.id);

    const getModuleName = () => {
        const path = location.pathname;

        // Handle special cases and formatting
        const segments = path.split('/').filter(segment => segment !== '');
        if (segments.length === 0) return 'Dashboard';

        const moduleName = segments[segments.length - 1];

        return moduleName.charAt(0).toUpperCase() + moduleName.slice(1).replace(/[-_]/g, ' ');
    };

    // Get the current module name to pass to the invite modal
    const getCurrentModuleName = () => {
        const path = location.pathname;
        const segments = path.split('/').filter(segment => segment !== '');

        if (segments.length === 0) return null; // Don't show for dashboard

        return segments[0].charAt(0).toUpperCase() + segments[0].toString().substring(1); // Return the raw module name for matching with Firestore
    };

    return (
        <>
            <div className="bg-transparent backdrop-blur-md w-full h-16 flex justify-between items-center px-6 mt-1">
                {/* Left Side - Logo and Module */}
                <div className="flex flex-row items-center">
                    <div onClick={() => navigate("/")} className="w-10 h-10 cursor-pointer bg-[#ffffff]/05 border border-[#ffffff]/15 backdrop-blur-lg p-0.5 rounded-md flex items-center justify-center">
                        <img src={Logo} className="object-contain h-full w-full" alt="CampusLM Logo" />
                    </div>
                    {showModule && location.pathname !== '/' &&
                        <div className="flex flex-row items-center space-x-2.5">
                            <LuChevronRight className={"ml-3 text-white opacity-30"}/>
                            <div className={"w-fit px-3 h-8 space-x-2 bg-[#ffffff]/05 cursor-pointer hover:bg-[#ffffff]/10 border border-[#ffffff]/15 backdrop-blur-lg rounded-md flex items-center justify-center transition-colors"}>
                                <div className={"text-white font-medium text-sm font-Poppins"}>{getModuleName()}</div>
                            </div>
                        </div>
                    }
                </div>

                {/* Right Side - Share, Divider, Assigned Users, Profile */}
                <div className="flex items-center space-x-4">
                    {/* Share Button */}
                    {showModule && location.pathname !== '/' && location.pathname !== '/settings' &&
                        <>
                            <button
                                onClick={handleShareClick}
                                className="w-fit px-3 h-8 space-x-2 bg-[#ffffff]/05 hover:bg-[#ffffff]/10 border border-[#ffffff]/15 backdrop-blur-lg rounded-md flex items-center justify-center transition-colors"
                                title="Share"
                            >
                                <div className={"text-white font-medium text-sm font-Poppins"}>Share</div>
                            </button>

                            {/* Vertical Divider */}
                            <div className="h-6 w-px bg-[#ffffff]/20"></div>
                        </>
                    }

                    {/* Assigned Users */}
                    {assignedUsers.length > 0 && (
                        <div className="flex items-center space-x-2">
                            <div className="flex -space-x-2">
                                {assignedUsers.slice(0, 3).map((user, index) => (
                                    <div
                                        key={user.id || index}
                                        className="relative"
                                        title={user.displayName || user.name || 'User'}
                                    >
                                        {user.photoURL ? (
                                            <img
                                                src={user.photoURL}
                                                className="w-8 h-8 rounded-full object-cover border-2 border-[#1a1a1a] bg-[#1a1a1a]"
                                                alt={user.displayName || user.name || 'User'}
                                            />
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#B0276E]/80 to-[#8a1f57]/80 flex items-center justify-center text-white font-medium text-xs border-2 border-[#1a1a1a]">
                                                {(user.displayName || user.name || 'U').charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {assignedUsers.length > 3 && (
                                    <div
                                        className="w-8 h-8 rounded-full bg-[#ffffff]/10 border-2 border-[#1a1a1a] flex items-center justify-center text-white/70 font-medium text-xs"
                                        title={`+${assignedUsers.length - 3} more users`}
                                    >
                                        +{assignedUsers.length - 3}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Vertical Divider (only show if there are assigned users) */}
                    {assignedUsers.length > 0 && (
                        <div className="h-6 w-px bg-[#ffffff]/20"></div>
                    )}

                    {/* Profile Photo */}
                    <div className="cursor-pointer"
                        onClick={handleProfileClick}
                    >
                        {currentUser && currentUser.photoURL && !imageError ? (
                            <img
                                src={currentUser.photoURL}
                                className="w-10 h-10 rounded-full object-cover border border-[#2e2e2e] backdrop-blur-sm"
                                onError={() => setImageError(true)}
                                alt={`${currentUser.displayName || 'User'}'s profile`}
                            />
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#B0276E]/80 to-[#8a1f57]/80 backdrop-blur-sm flex items-center justify-center text-white font-medium text-sm border-2 border-[#B0276E]/40">
                                {currentUser && currentUser.displayName ? currentUser.displayName.charAt(0).toUpperCase() : "U"}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Invite Modal */}
            <InviteModal
                isOpen={isInviteModalOpen}
                onClose={handleCloseInviteModal}
                moduleName={getCurrentModuleName()}
            />
        </>
    );
}